

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaChevronLeft, 
  FaChevronRight,
  FaSearch,
  FaSpinner
} from 'react-icons/fa';

import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/admin`;

const QuestionList = () => {
    const [questions, setQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [questionText, setQuestionText] = useState('');

    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [limit, setLimit] = useState(10); // Or whatever your default items per page is
     const [totalItems, setTotalItems] = useState(0);

    const fetchQuestions = useCallback(async (page = 1, pageSize = limit) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/questions?page=${page}&limit=${pageSize}`);
            if (response.data && response.data.questions) {
                setQuestions(response.data.questions);
                setTotalPages(response.data.totalPages || 0);
                 setTotalItems(response.data.totalItems || 0); // Store totalItems
                setCurrentPage(parseInt(response.data.page) || 1); // Ensure currentPage is updated from response
            } else {
                setQuestions([]);
                setTotalPages(0);
                  setTotalItems(0); // Reset totalItems
                setError('Failed to fetch questions: Invalid response format.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch questions.');
            console.error("Fetch error:", err);
            setQuestions([]); // Clear questions on error
            setTotalPages(0); // Reset total pages on error
        } finally {
            setIsLoading(false);
        }
    }, [limit]); // limit is a dependency if it can change

    useEffect(() => {
        fetchQuestions(currentPage, limit);
    }, [fetchQuestions, currentPage, limit]); // Refetch when currentPage or limit changes

    const handleOpenModalForAdd = () => {
        setCurrentQuestion(null);
        setQuestionText('');
        setError(null);
        setIsModalOpen(true);
    };

    const handleOpenModalForEdit = (question) => {
        setCurrentQuestion(question);
        setQuestionText(question.question);
        setError(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentQuestion(null);
        setQuestionText('');
        setError(null);
    };

    const handleSaveQuestion = async (e) => {
        e.preventDefault();
        if (!questionText.trim()) {
            setError("Question text cannot be empty.");
            return;
        }
        setIsLoading(true);
        setError(null);

        try {
            if (currentQuestion) {
                await axios.put(`${API_BASE_URL}/questions/${currentQuestion._id}`, { "question": questionText });
                toast.success("Question updated successfully");
            } else {
                await axios.post(`${API_BASE_URL}/questions`, { question: questionText });
                toast.success("Question added successfully");
            }
            // Fetch the current page again to see changes, especially if a new item might shift pagination
            fetchQuestions(currentPage, limit);
            handleCloseModal();
        } catch (err) {
            setError(err.response?.data?.message || (currentQuestion ? 'Failed to update question.' : 'Failed to add question.'));
            console.error("Save error:", err);
        } finally {
            setIsLoading(false);
        }
    };

const handleDeleteQuestion = async (id) => {
    const result = await Swal.fire({
        title: 'Are you sure you want to delete this question?',
        text: "This action cannot be undone.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
        customClass: {
            confirmButton: 'swal2-confirm-button',
            cancelButton: 'swal2-cancel-button'
        }
    });

    if (result.isConfirmed) {
        setIsLoading(true);
        setError(null);

        try {
            await axios.delete(`${API_BASE_URL}/questions/${id}`);
            toast.success("Question deleted successfully");

            if (questions.length === 1 && currentPage > 1) {
                setCurrentPage(prevPage => prevPage - 1);
            } else {
                fetchQuestions(currentPage, limit);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete question.');
            console.error("Delete error:", err);
            toast.error('Failed to delete question.');
        } finally {
            setIsLoading(false);
        }
    }
};


// const handleDeleteQuestion = (id) => {

//     const confirmDelete = () => {
//         setIsLoading(true);
//         setError(null);

//         axios.delete(`${API_BASE_URL}/questions/${id}`)
//             .then(() => {
//                 toast.success("Question deleted successfully");
//                 if (questions.length === 1 && currentPage > 1) {
//                     setCurrentPage(prevPage => prevPage - 1);
//                 } else {
//                     fetchQuestions(currentPage, limit);
//                 }
//             })
//             .catch((err) => {
//                 setError(err.response?.data?.message || 'Failed to delete question.');
//                 console.error("Delete error:", err);
//             })
//             .finally(() => {
//                 setIsLoading(false);
//             });
//     };

//     // Show confirmation toast
//     toast(
//         ({ closeToast }) => (
//             <div>
//                 <p>Are you sure you want to delete this question?</p>
//                 <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
//                     <button onClick={() => { confirmDelete(); closeToast(); }} style={{ background: 'red', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '4px' }}>
//                         Delete
//                     </button>
//                     <button onClick={closeToast} style={{ border: '1px solid gray', padding: '6px 10px', borderRadius: '4px' }}>
//                         Cancel
//                     </button>
//                 </div>
//             </div>
//         ),
//         { autoClose: false }
//     );
// };


    // --- Pagination Handlers ---
    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(prevPage => prevPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prevPage => prevPage - 1);
        }
    };

    const handleGoToPage = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    // --- Generate Page Numbers for Pagination UI ---
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxPagesToShow = 10; // Max page numbers to show around current page
        const halfMaxPages = Math.floor(maxPagesToShow / 2);

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            let startPage = currentPage - halfMaxPages;
            let endPage = currentPage + halfMaxPages;

            if (startPage <= 0) {
                endPage -= (startPage - 1);
                startPage = 1;
            }
            if (endPage > totalPages) {
                startPage -= (endPage - totalPages);
                endPage = totalPages;
            }
             // Ensure startPage is at least 1 after adjustments
            startPage = Math.max(startPage, 1);

            if (startPage > 1) {
                pageNumbers.push(1);
                if (startPage > 2) {
                    pageNumbers.push('...');
                }
            }

            for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i);
            }

            if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                    pageNumbers.push('...');
                }
                pageNumbers.push(totalPages);
            }
        }
        return pageNumbers;
    };


    return (
        <div className="min-h-full">
            {/* Header Section */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                            Question Management
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            Manage your questionnaire content
                        </p>
                    </div>
                    <button
                        onClick={handleOpenModalForAdd}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                    >
                        <FaPlus className="w-5 h-5" />
                        Add New Question
                    </button>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Questions Table */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
                {isLoading && questions.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full mb-4">
                            <FaSpinner className="animate-spin h-8 w-8 text-blue-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Loading Questions</h3>
                        <p className="text-gray-600 dark:text-gray-400">Please wait while we fetch your questions...</p>
                    </div>
                ) : !isLoading && questions.length === 0 && totalPages === 0 ? (
                    <div className="p-12 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                            <FaSearch className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Questions Found</h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">Get started by adding your first question</p>
                        <button
                            onClick={handleOpenModalForAdd}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors duration-200"
                        >
                            <FaPlus className="w-5 h-5" />
                            Add First Question
                        </button>
                    </div>
                ) : !isLoading && questions.length === 0 && totalPages > 0 ? (
                    <div className="p-12 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                            <FaSearch className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Questions on This Page</h3>
                        <p className="text-gray-600 dark:text-gray-400">Try navigating to a different page</p>
                    </div>
                ) : (
                    <>
                        {/* Table Header */}
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Questions ({totalItems})
                                </h3>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Table Content */}
                        <div className="overflow-x-auto overflow-y-auto max-h-96 w-full -mx-4 sm:mx-0">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr className='whitespace-nowrap'>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/12">
                                            #
                                        </th>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider w-10/12">
                                            Question Text
                                        </th>
                                        <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider w-2/12">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {questions.map((question, index) => (
                                        <tr key={question._id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                                {((currentPage - 1) * limit) + index + 1}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white break-words">
                                                        {question.question}
                                                    </p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => handleOpenModalForEdit(question)}
                                                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors duration-200"
                                                    >
                                                        <FaEdit className="w-4 h-4" />
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteQuestion(question._id)}
                                                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                                                    >
                                                        <FaTrash className="w-4 h-4" />
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 0 && !isLoading && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, totalItems)} of {totalItems} results
                        </div>
                        
                        <nav className="flex items-center gap-1" aria-label="Pagination">
                            <button
                                onClick={handlePreviousPage}
                                disabled={currentPage === 1 || isLoading}
                                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                <FaChevronLeft className="w-4 h-4" />
                                Previous
                            </button>
                            
                            <div className="flex items-center gap-1">
                                {getPageNumbers().map((page, index) => (
                                    typeof page === 'number' ? (
                                        <button
                                            key={index}
                                            onClick={() => handleGoToPage(page)}
                                            disabled={isLoading}
                                            className={`inline-flex items-center justify-center w-10 h-10 text-sm font-medium rounded-lg transition-colors duration-200 ${
                                                currentPage === page 
                                                    ? 'bg-blue-600 text-white shadow-lg' 
                                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    ) : (
                                        <span key={index} className="inline-flex items-center justify-center w-10 h-10 text-sm font-medium text-gray-500 dark:text-gray-400">
                                            {page}
                                        </span>
                                    )
                                ))}
                            </div>
                            
                            <button
                                onClick={handleNextPage}
                                disabled={currentPage === totalPages || isLoading}
                                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                Next
                                <FaChevronRight className="w-4 h-4" />
                            </button>
                        </nav>
                    </div>
                </div>
            )}


            {/* Modal for Add/Edit Question */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl">
                        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    {currentQuestion ? 'Edit Question' : 'Add New Question'}
                                </h2>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                    {currentQuestion ? 'Update the question text' : 'Create a new question for your questionnaire'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleCloseModal}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg p-2 transition-colors duration-200"
                                aria-label="Close modal"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                        
                        <form onSubmit={handleSaveQuestion} className="p-6">
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                                        Question Text
                                    </label>
                                    <textarea
                                        value={questionText}
                                        onChange={(e) => setQuestionText(e.target.value)}
                                        placeholder="Enter your question here..."
                                        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base transition-all duration-200 resize-y text-gray-900 dark:text-gray-100"
                                        rows="4"
                                        required
                                    />
                                </div>
                                
                                {error && isModalOpen && (
                                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                                        <div className="flex items-center">
                                            <svg className="h-5 w-5 text-red-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            <div className="flex justify-end space-x-3 mt-8">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                >
                                    {isLoading ? (
                                        <div className="flex items-center gap-2">
                                            <FaSpinner className="animate-spin h-4 w-4" />
                                            {currentQuestion ? 'Saving...' : 'Adding...'}
                                        </div>
                                    ) : (
                                        currentQuestion ? 'Save Changes' : 'Add Question'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuestionList;